/* eslint-disable react/prop-types */
import React, {useState, useCallback, useEffect} from 'react';
import NavigationLink from 'components/NavigationLink';
import {getAdminTabSlug, TRANSLATION_ADMIN_MENUS} from 'enums';
import UserIcon from 'assets/svgs/sidebar/user.svg';
import PostsIcon from 'assets/svgs/sidebar/post.svg';
import CompanyIcon from 'assets/svgs/sidebar/partner.svg';
import InformationIcon from 'assets/svgs/sidebar/information.svg';
import ReaderIcon from 'assets/svgs/sidebar/reader.svg';
import AdminIcon from 'assets/svgs/sidebar/admin.svg';
import JudgeIcon from 'assets/svgs/sidebar/judge.svg';
import NavigationIcon from 'assets/svgs/sidebar/navigation.svg';
import SidebarProfile from 'components/SidebarProfile';
// import { ReactComponent as UploadIcon } from 'assets/svgs/categories/upload.svg';
import {makeSelectBasicUser} from 'reducers/selectors/userSelectors';
import {
  makeSelectShowroomEligibility,
  makeSelectShowroomEligibilityFetching,
} from 'reducers/selectors/showroomSelectors';
import {useDispatch, useSelector} from 'react-redux';
import {useInjectSaga} from 'utils/injectSaga';
import showroomSaga from 'sagas/showroomSaga';
import {fetchShowroomEligibility} from 'actions/showroomActions';
import {canAccessPostWorkspace} from 'containers/Posts/reviewPolicy';
import messages from './messages';
import './sidebar.scss';
import EditProfile from './EditProfile';
import {
  isAdmin,
  isEditor,
  isJudge,
  isManager,
  isSeller,
  isStaffHasPermission,
} from '../../utils/role';
import JudgeSidebarItems from './JudgeSidebar';

const basicUserSelector = makeSelectBasicUser();
const isAdminNavigationActive = (match, {pathname}) => Boolean(match) &&
  !TRANSLATION_ADMIN_MENUS.some((label) => getAdminTabSlug(label) === pathname.split('/')[2]);

export default function Sidebar() {
  useInjectSaga({key: 'showroom', saga: showroomSaga});
  const dispatch = useDispatch();
  const userDetails = useSelector(basicUserSelector);
  // const roles = useSelector(state => state.roles.roles);
  // const role = roles
  //   ? roles.find(f => f._id === userDetails.masterRoleId._id)
  //   : null;
  // const role = userDetails.masterRoleId;
  const {roles} = userDetails;
  const showBlogPost = canAccessPostWorkspace(roles) || isStaffHasPermission(roles, ['project', 'product']);
  const showBlogCompany = isStaffHasPermission(roles, [
    'profile',
    'about',
    'partner',
    'cloud',
  ]);
  const showBlogInformation = canAccessPostWorkspace(roles) || isStaffHasPermission(roles, ['info']);
  const showBlog = showBlogPost || showBlogCompany || showBlogInformation;

  const showBoardReader = isAdmin(roles);
  const showBoardPartner = isAdmin(roles) || isSeller(roles);
  const showBoardAdmin = isAdmin(roles);
  const showBoardJudge = isAdmin(roles);
  const showBoard = showBoardReader || showBoardPartner || showBoardAdmin;
  const showUiTranslations = isAdmin(roles) || isEditor(roles);

  const showCategoryPost = isStaffHasPermission(roles, ['category']);
  const showCategoryCompany = isStaffHasPermission(roles, ['category']);
  const showCategoryInformation = isStaffHasPermission(roles, ['category']);
  const showCategory =
    showCategoryPost || showCategoryCompany || showCategoryInformation;
  const showroomEligibility = useSelector(makeSelectShowroomEligibility());
  const showroomEligibilityFetching = useSelector(
    makeSelectShowroomEligibilityFetching()
  );
  const authToken = userDetails?.authToken;
  const showIsAdmin = isAdmin(roles);
  const showIsShowroomStaff =
    showIsAdmin ||
    isManager(roles) ||
    showroomEligibility?.viewerRole === 'admin';
  const canShowShowroomEntry = showIsShowroomStaff;

  const [editProfile, setEditProfile] = useState(false);

  const handleProfileClick = useCallback(() => {
    setEditProfile((prev) => !prev);
  }, []);

  useEffect(() => {
    if (!authToken || showroomEligibilityFetching) return;

    const shouldFetchEligibility =
      !showroomEligibility ||
      (showIsShowroomStaff && showroomEligibility.viewerRole !== 'admin');

    if (shouldFetchEligibility) {
      dispatch(fetchShowroomEligibility());
    }
  }, [
    authToken,
    dispatch,
    showIsShowroomStaff,
    showroomEligibility,
    showroomEligibilityFetching,
  ]);

  const staffSidebar = () => (
    <>
      {showBlog ? (
        <ul className="pl-0 text-left mt-5 side-link bo_bottom">
          <p>{messages.blog}</p>
          {showBlogPost && (
            <li className="">
              <NavigationLink to="/posts/architectures" className="d-flex">
                <span>
                  <img src={PostsIcon} alt="" />
                </span>
                {messages.post}
                <b className="ml-auto mr-0">›</b>
              </NavigationLink>
            </li>
          )}
          {showBlogCompany && (
            <li className="">
              <NavigationLink to="/company" className="d-flex">
                <span>
                  <img src={CompanyIcon} alt="" />
                </span>
                {messages.company}
                <b className="ml-auto mr-0">›</b>
              </NavigationLink>
            </li>
          )}
          {showBlogInformation && (
            <li className="active">
              <NavigationLink to="/information-posts" className="d-flex">
                <span>
                  <img src={InformationIcon} alt="" />
                </span>
                {messages.information}
                <b className="ml-auto mr-0">›</b>
              </NavigationLink>
            </li>
          )}
        </ul>
      ) : null}

      {showBoard ? (
        <ul className="pl-0 text-left mt-5 side-link bo_bottom">
          <p>{messages.board}</p>
          {showBoardReader && (
            <li className="">
              <NavigationLink
                to="/readers"
                className="d-flex align-items-center"
              >
                <span>
                  <img src={ReaderIcon} alt="" />
                </span>
                {messages.reader}
                <b className="ml-auto mr-0">›</b>
              </NavigationLink>
            </li>
          )}
          {showBoardPartner && (
            <li className="">
              <NavigationLink
                to="/partners"
                className="d-flex align-items-center"
              >
                <span>
                  <img src={UserIcon} alt="" />
                </span>
                {messages.partner}
                <b className="ml-auto mr-0">›</b>
              </NavigationLink>
            </li>
          )}
          {showBoardAdmin && (
            <li className="">
              <NavigationLink to="/admin" isActive={isAdminNavigationActive} className="d-flex align-items-center">
                <span>
                  <img src={AdminIcon} alt="" />
                </span>
                {messages.admin}
                <b className="ml-auto mr-0">›</b>
              </NavigationLink>
            </li>
          )}
          {showBoardJudge && (
            <li className="">
              <NavigationLink to="/judge" className="d-flex align-items-center">
                <span>
                  <img src={JudgeIcon} alt="judge icon" />
                </span>
                {messages.judge}
                <b className="ml-auto mr-0">›</b>
              </NavigationLink>
            </li>
          )}
          {canShowShowroomEntry && (
            <li className="">
              <NavigationLink to="/showroom" className="d-flex align-items-center">
                <span>
                  <img src={PostsIcon} alt="" />
                </span>
                SHOWROOM
                <b className="ml-auto mr-0">›</b>
              </NavigationLink>
            </li>
          )}
        </ul>
      ) : null}

      {showCategory ? (
        <ul className="pl-0 text-left mt-5 side-link bo_bottom">
          <p>{messages.category}</p>
          {showCategoryPost && (
            <li>
              <NavigationLink to="/categories/posts" className="d-flex">
                <span>
                  <img src={PostsIcon} alt="" />
                </span>
                {messages.post}
                <b className="ml-auto mr-0">›</b>
              </NavigationLink>
            </li>
          )}
          {showCategoryCompany && (
            <li className="">
              <NavigationLink to="/categories/company" className="d-flex">
                <span>
                  <img src={CompanyIcon} alt="" />
                </span>
                {messages.company}
                <b className="ml-auto mr-0">›</b>
              </NavigationLink>
            </li>
          )}
          {showCategoryInformation && (
            <li>
              <NavigationLink to="/categories/information" className="d-flex">
                <span>
                  <img src={InformationIcon} alt="" />
                </span>
                {messages.information}
                <b className="ml-auto mr-0">›</b>
              </NavigationLink>
            </li>
          )}
        </ul>
      ) : null}
      {showUiTranslations && (
        <ul className="pl-0 text-left mt-5 side-link bo_bottom">
          <p>{messages.translation}</p>
          <li>
            <NavigationLink
              to="/ui-translations"
              className="d-flex align-items-center"
            >
              <span>
                <img src={NavigationIcon} alt="" />
              </span>
              {messages.uiTranslations}
              <b className="ml-auto mr-0">›</b>
            </NavigationLink>
          </li>
          {showIsAdmin && TRANSLATION_ADMIN_MENUS.map((label) => (
            <li key={label}>
              <NavigationLink
                to={`/admin/${getAdminTabSlug(label)}`}
                className="d-flex align-items-center"
              >
                <span>
                  <img src={NavigationIcon} alt="" />
                </span>
                {label}
                <b className="ml-auto mr-0">›</b>
              </NavigationLink>
            </li>
          ))}
        </ul>
      )}
    </>
  );

  const judgeSidebar = () => <JudgeSidebarItems />;

  const renderSidebar = () => {
    if (editProfile) {
      return (
        <EditProfile
          userDetails={userDetails}
          setEditProfile={setEditProfile}
        />
      );
    }
    if (isJudge(roles) && !canAccessPostWorkspace(roles)) return judgeSidebar();
    return staffSidebar();
  };
  return (
    <div className="side-nav">
      <SidebarProfile
        onClick={handleProfileClick}
      />

      {renderSidebar()}
    </div>
  );
}
